import { Zap, Server, Brain, Activity, Wifi, WifiOff, CreditCard, Coins } from 'lucide-react'

const providers = [
  { id: 'ollama', name: 'Ollama', host: 'Stomach', model: 'llama3.1', status: 'online', type: 'free', credits: '∞ local', icon: Server, color: 'text-aith-green' },
  { id: 'do-inference', name: 'DO Inference', host: 'DigitalOcean', model: 'DeepSeek / Llama', status: 'online', type: 'credits', credits: '$205.00', icon: Activity, color: 'text-aith-accent' },
  { id: 'workers-ai', name: 'Workers AI', host: 'Cloudflare', model: 'Various', status: 'online', type: 'free', credits: 'Workers plan', icon: Wifi, color: 'text-aith-cyan' },
  { id: 'huggingface', name: 'HuggingFace', host: 'HF PRO', model: 'Athena Brain (Qwen/Claude 40B)', status: 'online', type: 'paid', credits: '20x credits | ZeroGPU', icon: Brain, color: 'text-aith-purple' },
  { id: 'openrouter', name: 'OpenRouter', host: 'OpenRouter', model: 'Aggregated', status: 'offline', type: 'paid', credits: 'API key ready', icon: Wifi, color: 'text-gray-400' },
  { id: 'google-cloud', name: 'Google Cloud', host: 'Vertex AI', model: 'Gemini / PaLM', status: 'offline', type: 'paid', credits: 'Enterprise', icon: Server, color: 'text-aith-yellow' },
  { id: 'alibaba', name: 'Alibaba PAI', host: 'Bangkok ECS', model: 'Qwen / Llama', status: 'offline', type: 'paid', credits: 'Pay-as-you-go', icon: Server, color: 'text-aith-orange' },
  { id: 'tencent', name: 'Tencent TI', host: 'Tencent Cloud', model: 'Various', status: 'offline', type: 'paid', credits: 'Enterprise', icon: Wifi, color: 'text-aith-red' },
  { id: 'nvidia-nim', name: 'NVIDIA NIM', host: 'NVIDIA Cloud', model: 'Llama / Mistral', status: 'offline', type: 'paid', credits: 'API key ready', icon: Brain, color: 'text-green-400' },
  { id: 'mistral', name: 'Mistral AI', host: 'Mistral', model: 'Mistral Large', status: 'offline', type: 'paid', credits: 'API key ready', icon: Brain, color: 'text-aith-cyan' },
  { id: 'lmstudio', name: 'LM Studio', host: 'Local (Mac/iMac)', model: 'Any GGUF', status: 'offline', type: 'free', credits: 'Local only', icon: Server, color: 'text-gray-500' },
  { id: 'openwebui', name: 'OpenWebUI', host: 'Self-hosted', model: 'Ollama backend', status: 'offline', type: 'free', credits: 'Self-hosted', icon: Wifi, color: 'text-gray-500' },
]

function StatusBadge({ status, type, credits }) {
  const statusColors = {
    online: 'bg-aith-green/10 text-aith-green border-aith-green/30',
    offline: 'bg-gray-800 text-gray-500 border-gray-700',
  }
  const typeLabels = {
    free: 'FREE',
    paid: 'PAID',
    credits: 'CREDITS',
  }
  const typeColors = {
    free: 'bg-aith-green/20 text-aith-green',
    paid: 'bg-aith-red/10 text-aith-red',
    credits: 'bg-aith-yellow/10 text-aith-yellow',
  }
  return (
    <div className="flex items-center gap-2">
      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${statusColors[status]}`}>
        {status.toUpperCase()}
      </span>
      <span className={`text-[10px] px-1.5 py-0.5 rounded ${typeColors[type]}`}>
        {typeLabels[type]}
      </span>
      <span className="text-[10px] text-gray-500 flex items-center gap-1">
        {type === 'free' ? <Coins className="w-3 h-3" /> : <CreditCard className="w-3 h-3" />}
        {credits}
      </span>
    </div>
  )
}

export default function InferencePage() {
  const onlineCount = providers.filter(p => p.status === 'online').length
  const freeCount = providers.filter(p => p.type === 'free').length
  const paidCount = providers.filter(p => p.type === 'paid').length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Zap className="w-5 h-5 text-aith-accent" />
          <h2 className="text-sm font-bold tracking-widest text-gray-500 uppercase">Inference Engine</h2>
        </div>
        <div className="flex gap-3 text-[10px]">
          <span className="text-aith-green">{onlineCount} ONLINE</span>
          <span className="text-gray-600">|</span>
          <span className="text-gray-400">{freeCount} FREE</span>
          <span className="text-gray-600">|</span>
          <span className="text-aith-red">{paidCount} PAID</span>
        </div>
      </div>

      {/* Provider Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {providers.map((provider) => {
          const Icon = provider.icon
          const StatusIcon = provider.status === 'online' ? Wifi : WifiOff
          return (
            <div key={provider.id} className="panel hover:border-aith-accent/30 transition-colors group">
              <div className="flex items-start justify-between mb-3">
                <div className={`p-2 rounded bg-gray-800/50 ${provider.color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <StatusIcon className={`w-3.5 h-3.5 ${provider.status === 'online' ? 'text-aith-green' : 'text-gray-600'}`} />
              </div>
              <h3 className="text-sm font-bold text-white mb-0.5">{provider.name}</h3>
              <p className="text-[10px] text-gray-500 mb-2">{provider.host} · {provider.model}</p>
              <StatusBadge status={provider.status} type={provider.type} credits={provider.credits} />
            </div>
          )
        })}
      </div>

      {/* Legend / Notes */}
      <div className="panel mt-4">
        <h3 className="text-xs font-bold text-gray-400 mb-2">Routing Rules & The Triad</h3>
        <div className="space-y-1 text-[10px] text-gray-500">
          <p>· <span className="text-aith-green">Local (Ollama)</span> → Default for privacy / offline mode. Solo's direct line.</p>
          <p>· <span className="text-aith-accent">DO Inference</span> → Fast inference, use credits first ($205). ZZ's engine.</p>
          <p>· <span className="text-aith-cyan">Workers AI</span> → Fallback, edge-deployed models. Infrastructure layer.</p>
          <p>· <span className="text-aith-purple">HuggingFace / Athena</span> → Vision, critique, alignment. Athena's brain. 40B Qwen/Claude fusion. ZeroGPU RTX Pro 6000.</p>
          <p>· <span className="text-gray-400">Paid providers</span> → Billed per-token. Specific models only.</p>
          <p className="text-gray-600 mt-2 italic">The Triad: Solo decides → Athena verifies → ZZ executes. Athena never commands. ZZ never wakes without Athena watching.</p>
        </div>
      </div>

    </div>
  )
}

